import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import type { AppType } from '../types/AppType';
import type { Environment } from '../types/Environment';
import { getRequiredEnvironmentKeys } from './getRequiredEnvironmentKeys';
import { getEnvironmentValue } from './getEnvironmentValue';

/**
 * @group Functions
 * @summary <blockquote>Validates that all required environment variables are present for a specific app type and environment combination.</blockquote>
 * 
 * This function is used during build-time validation to ensure all required environment variables
 * are set before the build process starts. It checks each required variable against `process.env`
 * and collects all missing variables, returning a detailed error with the complete list.
 * 
 * @param appType - The application type ('web' or 'mobile')
 * @param environment - The environment ('development', 'preview', or 'production')
 * @returns Result<void, AppError> - Success if all required vars are present, error with list of missing vars if any are missing
 * 
 * @example
 * ```typescript
 * import { validateRequiredEnvironment } from '@shared/utils';
 * 
 * // Validate web production environment
 * const result = validateRequiredEnvironment('web', 'production');
 * 
 * result.match({
 *   ok: () => {
 *     console.log('✅ All required environment variables are present');
 *   },
 *   err: (error) => {
 *     console.error('❌ Missing required environment variables:');
 *     console.error(error.errorMessage);
 *     process.exit(1);
 *   }
 * });
 * ```
 * 
 * **Build-Time Usage:**
 * - Called by build scripts before starting the build process
 * - Fails fast with clear error messages listing all missing variables
 * - Should exit with non-zero code if validation fails
 * - Emits analytics event on failure (Step 5)
 */
export function validateRequiredEnvironment(
  appType: AppType,
  environment: Environment
): Result<void, AppError> {
  // Step 1: Get required vars from registry
  const requiredKeysResult = getRequiredEnvironmentKeys(appType, environment);
  if (requiredKeysResult.isErr) {
    return err(requiredKeysResult.getFail());
  }
  const requiredKeys = requiredKeysResult.value;
  
  // Step 2: If no required vars, validation passes
  if (requiredKeys.length === 0) {
    return ok(undefined);
  }
  
  // Step 3: Check each var against process.env
  const missingVars: string[] = [];
  
  for (const key of requiredKeys) {
    const valueResult = getEnvironmentValue(key, false);
    
    if (valueResult.isErr) {
      // Variable is missing
      missingVars.push(key);
    }
  }
  
  // Step 4: If any missing, return error with list
  if (missingVars.length > 0) {
    const missingVarsList = missingVars.map(v => `  - ${v}`).join('\n');
    
    return err({
      errorCode: 'MISSING_REQUIRED_ENV_VARS',
      errorMessage: `Missing ${missingVars.length} required environment variable(s) for ${appType}/${environment}:\n${missingVarsList}\n\nPlease set these variables before building.`
    });
  }
  
  // Step 5: All vars present - validation passes
  return ok(undefined);
}

